/* StepIndustry — Wizard Step 2.
 * Pick the industry (only Beauty for now) + sub-categories. The choice
 * pre-seeds field-visibility defaults + suggested category list.
 * Other industries can be added by extending catalog_industry_presets.py. */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Check, Sparkles, ChevronLeft } from "lucide-react";

export default function StepIndustry({ value, onChange, onNext, onBack }) {
  const [industries, setIndustries] = useState([]);
  const [detail, setDetail] = useState(null);    // full preset for the picked industry
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/seller/catalog-setup/industries");
        const list = r.data?.industries || [];
        setIndustries(list);
        // Pre-pick Beauty if it's the only option and the seller hasn't chosen yet
        if (list.length === 1 && !value.industry_id) {
          onChange({ industry_id: list[0].id, subcategories: value.subcategories || [] });
        }
      } catch (_e) { /* ignore */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!value.industry_id) { setDetail(null); return; }
    api.get(`/seller/catalog-setup/industries/${value.industry_id}`)
       .then(r => setDetail(r.data))
       .catch(() => setDetail(null));
  }, [value.industry_id]);

  const toggleSub = (subId) => {
    const cur = new Set(value.subcategories || []);
    if (cur.has(subId)) cur.delete(subId); else cur.add(subId);
    onChange({ industry_id: value.industry_id, subcategories: [...cur] });
  };

  const canContinue = !!value.industry_id && (value.subcategories || []).length > 0;

  return (
    <div data-testid="setup-step-industry">
      <div className="text-[10px] mono uppercase tracking-[0.24em] text-foreground/55">Step 1 of 4 · Industry</div>
      <h1 className="display text-3xl mt-2 tracking-tight">What's your industry?</h1>
      <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
        We'll pre-seed sensible defaults — field visibility, category list, recommended template — so you don't start from scratch. Override any of it later.
      </p>

      {/* Industry tiles */}
      <div className="mt-7">
        <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">Industry</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {loading
            ? <div className="text-foreground/45 text-sm">Loading…</div>
            : industries.map(ind => {
              const active = value.industry_id === ind.id;
              return (
                <button key={ind.id}
                  onClick={() => onChange({ industry_id: ind.id, subcategories: [] })}
                  data-testid={`industry-${ind.id}`}
                  className={`text-left rounded-2xl border p-4 transition-all ${active ? "border-foreground bg-foreground/[0.03] ring-2 ring-foreground/10" : "border-border bg-white hover:border-foreground/40"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`w-10 h-10 rounded-lg grid place-items-center ${active ? "bg-foreground text-background" : "bg-secondary text-foreground/65"}`}>
                      <Sparkles className="w-4.5 h-4.5"/>
                    </span>
                    {active && <Check className="w-4 h-4 text-foreground"/>}
                  </div>
                  <div className="mt-3 font-medium text-sm">{ind.label}</div>
                  <div className="text-[11px] text-foreground/55 mt-1 leading-relaxed">{ind.tagline}</div>
                </button>
              );
            })}
        </div>
      </div>

      {/* Subcategories */}
      {detail && (
        <div className="mt-7" data-testid="industry-subcategories">
          <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">
            Which {detail.label} segments do you cover?
          </div>
          <p className="text-[12px] text-foreground/55 mb-3">
            Pick all that apply. Each one adds suggested categories to your catalog (you can edit them later).
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {detail.subcategories.map(s => {
              const on = (value.subcategories || []).includes(s.id);
              return (
                <button key={s.id} onClick={() => toggleSub(s.id)}
                  data-testid={`sub-${s.id}`}
                  className={`text-left rounded-xl border p-3 transition-colors ${on ? "border-foreground bg-foreground/[0.03]" : "border-border bg-white hover:border-foreground/40"}`}>
                  <div className="flex items-center gap-2">
                    <span className={`shrink-0 w-4 h-4 rounded border grid place-items-center ${on ? "bg-foreground border-foreground" : "bg-white border-border"}`}>
                      {on && <Check className="w-3 h-3 text-background"/>}
                    </span>
                    <span className="text-[13px] font-medium">{s.label}</span>
                  </div>
                  <div className="text-[10.5px] text-foreground/55 mt-1 leading-snug pl-6">
                    {(s.suggested_categories || []).slice(0, 4).join(" · ")}
                    {(s.suggested_categories || []).length > 4 && ` · +${s.suggested_categories.length - 4} more`}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button onClick={onBack} className="h-10 px-4 rounded-full text-sm text-foreground/65 hover:text-foreground inline-flex items-center gap-1.5"><ChevronLeft className="w-3.5 h-3.5"/>Back</button>
        <button onClick={onNext} disabled={!canContinue} data-testid="industry-next"
          className="h-11 px-5 rounded-full bg-foreground text-background hover:bg-foreground/90 text-sm font-medium disabled:opacity-50">
          Continue →
        </button>
      </div>
    </div>
  );
}
